import axios from 'axios';
import { computed, ref, Ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';
import { ErrorResponse, Loadable } from '@rygen/rygen-ui/model';

import api from '@/api';
import {
  DocumentProperties,
  EmailDocumentCommand,
  Order,
  SignedDocument,
} from '@/model';
import { useParentEntityStore } from '@/stores/ParentEntity';
import { updateValueInPlace, getContentTypeFor } from '@/util';

import { useSelectedDomain } from './useSelectedDomain';

const documentsByOrder: Ref<Record<number, SignedDocument[]>> = ref({});
const documentsByShipment: Ref<Record<number, SignedDocument[]>> = ref({});

type Payload = Loadable<SignedDocument[]>;

interface useDocumentsResponse {
  deleteDocument: (document: SignedDocument) => Promise<void>;
  documents: Ref<Payload>;
  emailDocuments: (
    documents: SignedDocument[],
    sendBol: boolean,
    emails: string,
  ) => Promise<void>;
  loadDocuments: () => Promise<Ref<Payload>>;
  numDocuments: Ref<number | undefined>;
  saveDocument: (document: SignedDocument) => Promise<void>;
}

export const useDocuments = (): useDocumentsResponse => {
  const { t } = useI18n();
  const watchSelectedDomain = useSelectedDomain();
  const toast = useToast();
  const { parentEntity } = useParentEntityStore();

  const isOrder = computed(() => parentEntity.value instanceof Order);
  const docsCache = computed(() =>
    isOrder.value ? documentsByOrder.value : documentsByShipment.value,
  );
  const isLoading = ref(false);
  const error: Ref<unknown> = ref();
  const documents: Ref<Payload> = computed(() => {
    const entity = parentEntity.value;
    return {
      data: entity?.id ? docsCache.value[entity?.id] : undefined,
      error: error.value,
      loading: isLoading.value,
    };
  });
  const numDocuments = computed(() => documents.value.data?.length);

  const loadDocuments = async (): Promise<Ref<Payload>> => {
    const entity = parentEntity.value;
    if (entity?.id == null) {
      throw new Error('Missing id for Order/Shipment');
    }

    const getDocuments = computed(() =>
      isOrder.value
        ? api.orderDocument.getDocumentsByOrderId.bind(api.orderDocument)
        : api.shipmentDocument.getDocumentsByShipmentId.bind(
            api.shipmentDocument,
          ),
    );

    isLoading.value = true;
    try {
      const docs = await getDocuments.value(entity.id);
      docsCache.value[entity.id] = docs;
      watchSelectedDomain(loadDocuments);
    } catch (e) {
      if (axios.isCancel(e)) {
        return documents;
      }

      console.error('Error loading documents', e);
      error.value = e;
      toast.addError({
        detail: t('errors.loadingParam', {
          param: t('documents.documents', 2),
        }),
        error: e,
      });
    } finally {
      isLoading.value = false;
    }

    return documents;
  };

  const emailDocuments = async (
    documents: SignedDocument[],
    sendBol: boolean,
    emails: string,
  ) => {
    try {
      isLoading.value = true;
      if (!parentEntity.value?.id) {
        throw new Error('Could not determine parent id');
      }

      const emailDocuments = isOrder.value
        ? api.orderDocument.emailDocuments.bind(api.orderDocument)
        : api.shipmentDocument.emailDocuments.bind(api.shipmentDocument);
      const emailDocumentCommand: EmailDocumentCommand = {
        documents,
        sendBol,
        emailInfo: { emails },
      };
      await emailDocuments(parentEntity.value.id, emailDocumentCommand);
      toast.addSuccess({
        detail: t('messages.successEmailingParam', {
          param: t('documents.documents', 2),
        }),
      });
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      let errorDetail = '';
      if (axios.isAxiosError(e)) {
        const errorResponse = e.response?.data as ErrorResponse;
        errorDetail = errorResponse.exceptionMessage;
      }

      console.error('Error emailing documents', e);
      error.value = e;
      toast.addError({
        detail: t('errors.emailingParam', {
          param:
            t('documents.documents', 2) +
            (errorDetail ? ` - ${errorDetail}` : ''),
        }),
        error: e,
      });
    } finally {
      isLoading.value = false;
    }
  };

  const getDocumentId = (document: SignedDocument) => {
    return document.id ?? -1;
  };

  const saveDocument = async (document: SignedDocument) => {
    const attachDocument = isOrder.value
      ? api.orderDocument.attachDocument.bind(api.orderDocument)
      : api.shipmentDocument.attachDocument.bind(api.shipmentDocument);
    const getUploadUrl = isOrder.value
      ? api.orderDocument.getUploadUrl.bind(api.orderDocument)
      : api.shipmentDocument.getUploadUrl.bind(api.shipmentDocument);
    const updateDocument = isOrder.value
      ? api.orderDocument.updateDocument.bind(api.orderDocument)
      : api.shipmentDocument.updateDocument.bind(api.shipmentDocument);

    try {
      isLoading.value = true;
      if (!parentEntity.value?.id) {
        throw new Error('Could not determine parent id');
      }

      let fileName = document.fileName ?? document.file?.name ?? 'unknown';
      const contentType =
        document.contentType ||
        (document.file && getContentTypeFor(document.file)) ||
        'unknown';
      const contentLength = document.contentLength
        ? Number(document.contentLength)
        : (document.file?.size ?? 0);

      // User is uploading a file
      if (document.file) {
        const { fileName: gcpFileName, uploadUrl } = await getUploadUrl(
          parentEntity.value.id,
          { fileName, contentType },
        );
        fileName = gcpFileName;
        document.fileName = gcpFileName;

        await axios.put(uploadUrl, document.file, {
          headers: {
            'Content-Type': contentType,
          },
        });
      }

      const documentProperties = new DocumentProperties({
        documentName: document.documentName,
        documentTypes: document.types,
        fileName,
        contentType,
        contentLength,
      });

      let savedDocument;
      if (document.id) {
        savedDocument = await updateDocument(
          parentEntity.value.id,
          document.id,
          documentProperties,
        );
      } else {
        savedDocument = await attachDocument(
          parentEntity.value.id,
          documentProperties,
        );
      }

      if (docsCache.value[parentEntity.value.id]) {
        updateValueInPlace(
          docsCache.value[parentEntity.value.id],
          savedDocument,
          getDocumentId,
        );
      } else {
        docsCache.value[parentEntity.value.id] = [savedDocument];
      }

      toast.addSuccess({
        detail: t('messages.successUploadingParam', {
          param: t('documents.documents'),
        }),
      });
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      console.error('Error uploading document', e);
      error.value = e;
      toast.addError({
        detail: t('errors.savingParam', {
          param: t('documents.documents'),
        }),
        error: e,
      });
    } finally {
      isLoading.value = false;
    }
  };

  const deleteDocument = async (document: SignedDocument) => {
    try {
      isLoading.value = true;
      if (!parentEntity.value || parentEntity.value.id == null) {
        throw new Error('Missing id for Order/Shipment');
      }
      if (!document.id) {
        throw new Error('Missing id for document');
      }

      const removeDocument = isOrder.value
        ? api.orderDocument.removeDocument.bind(api.orderDocument)
        : api.shipmentDocument.removeDocument.bind(api.shipmentDocument);
      await removeDocument(parentEntity.value.id, document.id);

      const docs = docsCache.value[parentEntity.value.id];
      if (docs) {
        const index = docs.findIndex((d) => d.id === document.id);
        docs.splice(index, 1);
      }

      toast.addSuccess({
        detail: t('messages.successDeletingParam', {
          param: t('documents.documents'),
        }),
      });
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      error.value = e;
      toast.addError({
        detail: t('errors.deletingParam', {
          param: t('documents.documents'),
        }),
        error: e,
      });
    } finally {
      isLoading.value = false;
    }
  };

  return {
    deleteDocument,
    documents,
    emailDocuments,
    loadDocuments,
    numDocuments,
    saveDocument,
  };
};
